import { eventBus } from '../core/EventBus';

export interface Tool {
  id: string;
  name: string;
  icon: string;
  cursor: string;
}

export const TOOLS: Tool[] = [];

export class InputHandler {
  private canvas: HTMLCanvasElement;
  private currentTool: string | null = null;
  private onMouseMove?: (x: number, y: number) => void;
  private plantingMode: boolean = false;
  private lastMouseX = -1;
  private lastMouseY = -1;

  constructor(canvas: HTMLCanvasElement, onMouseMove?: (x: number, y: number) => void) {
    this.canvas = canvas;
    this.onMouseMove = onMouseMove;
    this.setupListeners();
  }

  private setupListeners(): void {
    this.canvas.addEventListener('click', this.handleClick.bind(this));
    this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.canvas.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    this.canvas.addEventListener('contextmenu', (e) => {
      if (this.plantingMode) {
        e.preventDefault();
        this.cancelPlacing();
      }
    });

    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private handleClick(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (this.plantingMode) {
      eventBus.emit('plant:place', x, y);
      return;
    }

    if (!this.currentTool) {
      eventBus.emit('canvas:click', x, y);
      return;
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.lastMouseX = x * (this.canvas.width / rect.width);
    this.lastMouseY = y * (this.canvas.height / rect.height);

    if (this.onMouseMove) {
      this.onMouseMove(x, y);
    }
  }

  private handleMouseLeave(): void {
    this.lastMouseX = -1;
    this.lastMouseY = -1;
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

    switch (e.key) {
      case 'p':
      case 'P':
        if (this.plantingMode) {
          this.cancelPlacing();
        } else {
          this.startPlacing();
        }
        break;
      case 'Escape':
        if (this.plantingMode) {
          this.cancelPlacing();
        } else {
          this.selectTool(null);
        }
        break;
    }
  }

  selectTool(toolId: string | null): void {
    this.currentTool = toolId;
    if (this.plantingMode && toolId !== null) {
      this.cancelPlacing();
    }
    const tool = TOOLS.find(t => t.id === toolId);
    this.canvas.style.cursor = tool ? tool.cursor : 'default';
    eventBus.emit('tool:selected', toolId);
  }

  getCurrentTool(): string | null {
    return this.currentTool;
  }

  startPlacing(): void {
    this.plantingMode = true;
    this.canvas.style.cursor = 'crosshair';
    eventBus.emit('plant:placingMode', true);
  }

  cancelPlacing(): void {
    this.plantingMode = false;
    this.canvas.style.cursor = 'default';
    eventBus.emit('plant:placingMode', false);
  }

  isPlacing(): boolean {
    return this.plantingMode;
  }

  getLastMousePos(): { x: number; y: number } | null {
    if (this.lastMouseX < 0 || this.lastMouseY < 0) return null;
    return { x: this.lastMouseX, y: this.lastMouseY };
  }
}
